import { useEffect, useState } from 'react'
import { FilePen, Trash2, X } from 'lucide-react'

type Props = {
  // An open event's unsaved changes, or a new event with something in it.
  kind: 'changes' | 'draft'
  // A place or profile, from Lugares or Perfis (PRD.md, item 98), or a
  // category or theme, from Categorias e temas.
  entity?: 'event' | 'place' | 'producer' | 'taxonomy'
  onDrop: () => Promise<void> | void
  // Saving the draft, for a new event.
  onKeep?: () => Promise<void> | void
  onClose: () => void
}

// Leaving the editor with something pending (PRD.md, item 73), asked as
// Shopify does for an open product and as Gmail and Outlook do for a draft.
// Closing keeps editing.
const WHERE = {
  event: { kept: 'O evento continua como está no app.', list: 'na página Eventos' },
  place: { kept: 'O lugar continua como está.', list: 'na página Lugares' },
  producer: { kept: 'O perfil continua como está no app.', list: 'na página Perfis' },
  taxonomy: { kept: 'O que já estava salvo continua como está no app.', list: 'na página Categorias e temas' },
}

export function LeaveDialog({ kind, entity = 'event', onDrop, onKeep, onClose }: Props) {
  const [busy, setBusy] = useState(false)
  const act = (action: () => Promise<void> | void) => async () => {
    setBusy(true)
    try { await action() } finally { setBusy(false) }
  }

  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose() }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [busy, onClose])

  return (
    <div className="dialog-backdrop" role="presentation" onClick={() => !busy && onClose()}>
      <section className="dialog dialog--compact" role="dialog" aria-modal="true" aria-labelledby="leave-title" onClick={(event) => event.stopPropagation()}>
        <div className="dialog__heading">
          <div><h2 id="leave-title">{kind === 'changes' ? 'Sair sem salvar?' : 'Salvar como rascunho?'}</h2></div>
          <button type="button" className="icon-button" aria-label="Fechar" disabled={busy} onClick={onClose}><X size={18} /></button>
        </div>
        <div className="dialog__body">
          <p>{kind === 'changes'
            ? `As alterações feitas aqui se perdem. ${WHERE[entity].kept}`
            : `O rascunho fica em Rascunhos, ${WHERE[entity].list}, para continuar depois.`}</p>
        </div>
        <div className="dialog__actions">
          {kind === 'changes' ? <>
            <button type="button" className="button button--secondary" disabled={busy} onClick={onClose}>Continuar editando</button>
            <button type="button" className="button button--destructive" disabled={busy} onClick={act(onDrop)}>Descartar alterações</button>
          </> : <>
            <button type="button" className="button button--danger" disabled={busy} onClick={act(onDrop)}><Trash2 size={17} /> Excluir</button>
            <button type="button" className="button button--primary" disabled={busy} onClick={onKeep && act(onKeep)}><FilePen size={17} /> Salvar rascunho</button>
          </>}
        </div>
      </section>
    </div>
  )
}
